import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { calendarApi } from './api';
import CalendarEventDialog from './CalendarEventDialog';
import { addDays, addMinutesWall, dateToWall, fcNow, todayWall, wallDate, wallDiffMinutes } from './calendarTime';
import { TODAY_RED, isTodayRed } from './calendarToday';
import { shortTimeFormat } from './calendarPanes';
import './calendar.css';

// 5.32.0 Calendar: Month / Week / Day / List of the viewed person's calendars + Office (colour per calendar,
// legend toggles in CalendarApp). Click a day / time slot -> New event; click an event -> view / edit / delete /
// copy. Non-repeating events can be dragged; a repeating one is changed in its dialog (whole series).
// FullCalendar runs in timeZone "UTC" fed with Chicago wall strings — see calendarTime.js.
// 5.33.0: opens in Month (no longer the last view from localStorage). PANE mode (Side by side): `calendarId` = the
// one calendar shown, `paneChoices` = the "New event" calendars (pane's own first), no own toolbar — the shared
// toolbar in CalendarApp drives every pane through `onApi`; `onChanged` after any write refreshes all panes.

// 5.34.0: today's occurrences of the login's OWN Personal calendar (overview.me.personal_calendar_id) are RED in
// every view and layout (Office / shared / other people's never); the view refetches when the Chicago date changes.
// `openRequest` = {objectId} opens that event's dialog once (the Today panel's "open this event").
// 5.36.0 fitting on 1080p: short times (9a, 9:30a, 2p); Month lines = dot + time + title, the title wraps to 2 lines
// before it is cut (full title on hover); `showAll` (default on) lets Month day cells grow to fit every event
// instead of "+N more". Text size comes from CalendarApp (CSS var --idcal-fs on a parent).

// Month-grid timed one-day events ("dot" events): dot + short time + title in one block that wraps to 2 lines.
// Everything else (all-day / multi-day bars, Week, Day, List) keeps FullCalendar's own rendering.
function monthEventContent(arg) {
  const ev = arg.event;
  if (!arg.view.type.startsWith('dayGrid') || ev.allDay) return true;
  if (ev.end && ev.start && ev.end.getTime() - ev.start.getTime() > 0
    && ev.end.toISOString().slice(0, 10) !== ev.start.toISOString().slice(0, 10)
    && ev.end.toISOString().slice(11, 19) !== '00:00:00') return true;        // spans days: drawn as a bar
  return (
    <div className="idcal-ev">
      <span className="fc-daygrid-event-dot" style={{ borderColor: arg.borderColor || arg.backgroundColor }} />
      <div className="idcal-ev-text">{arg.timeText && <span className="idcal-ev-time">{arg.timeText}</span>}{arg.timeText ? ' ' : ''}{ev.title}</div>
    </div>
  );
}

// 5.37.1 Overlay for admins: `extraSources` = [{user, calendarId}] — other people's calendars ticked in the legend,
// each fetched through that person's view (the existing admin-only ?user= path, same as a side-by-side person pane).
// Their events open / save / move / delete / copy as that person (?user=); new events still go to the login's own
// view. `overview.calendars` may include those calendars with `user` set (never offered as a "New event" choice).
export default function CalendarView({ target, overview, colors, hidden, reloadKey, onChanged,
  calendarId, paneChoices, onApi, initialView, initialDate, onDatesSet, openRequest, showAll = true, extraSources }) {
  const paneMode = !!calendarId;
  const ref = useRef(null);
  const [dialog, setDialog] = useState(null);     // {mode, detail?, createDefaults?, error?, notice?}
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');
  const hiddenRef = useRef(hidden);
  hiddenRef.current = hidden;

  const calendars = useMemo(() => (overview ? overview.calendars : []), [overview]);
  const calById = useMemo(() => Object.fromEntries(calendars.map((c) => [c.id, c])), [calendars]);
  const myPersonalId = overview?.me?.personal_calendar_id || null;
  // "mine" for a new event = the viewed person's Personal; plus Office.
  const choices = useMemo(() => paneChoices || calendars.filter((c) => !c.user && c.writable && c.components.includes('VEVENT')
    && (c.kind === 'personal' || c.kind === 'office')).sort((a, b) => (a.kind === 'personal' ? -1 : b.kind === 'personal' ? 1 : 0)), [calendars, paneChoices]);

  const refetch = useCallback(() => { const api = ref.current && ref.current.getApi(); if (api) api.refetchEvents(); }, []);
  const extraRef = useRef(extraSources || []);
  extraRef.current = extraSources || [];
  const extraKey = (extraSources || []).map((x) => `${x.user}:${x.calendarId}`).join(',');
  useEffect(() => { refetch(); }, [hidden, reloadKey, target, extraKey, refetch]);
  const onApiRef = useRef(onApi);
  onApiRef.current = onApi;
  useEffect(() => {
    if (onApiRef.current) onApiRef.current(ref.current ? ref.current.getApi() : null);
    return () => { if (onApiRef.current) onApiRef.current(null); };
  }, []);
  useEffect(() => {                                  // 5.34.0 red follows the Chicago date
    let day = todayWall();
    const id = setInterval(() => { const d = todayWall(); if (d !== day) { day = d; refetch(); } }, 60000);
    return () => clearInterval(id);
  }, [refetch]);

  const fetchEvents = useCallback((info, ok, fail) => {
    const s = dateToWall(info.start);
    const en = dateToWall(info.end);
    const tag = (user) => (r) => ({ user, data: r.data });
    Promise.all([calendarApi.events(s, en, target, calendarId).then(tag(target)),
      ...extraRef.current.map((x) => calendarApi.events(s, en, x.user, x.calendarId).then(tag(x.user)))])
      .then((parts) => {
        const skipped = parts.reduce((n, p) => n + (p.data.skipped || 0), 0);
        setLoadError(skipped ? `${skipped} item(s) could not be shown` : '');
        const today = todayWall();
        const all = parts.flatMap((p) => p.data.events.map((e) => ({ ...e, _user: p.user })));
        ok(all.filter((e) => !hiddenRef.current.has(e.calendar_id)).map((e) => {
          const cal = calById[e.calendar_id] || {};
          const red = isTodayRed(e, myPersonalId, today);
          const color = red ? TODAY_RED : (colors[e.calendar_id] || '#64748b');
          return {
            id: e.id, title: (e.recurring ? '🔁 ' : '') + e.title, start: e.start, end: e.end, allDay: e.allDay,
            backgroundColor: color, borderColor: color, classNames: red ? ['idcal-today'] : [],
            editable: !!cal.writable && !e.recurring,
            extendedProps: { objectId: e.object_id, etag: e.etag, location: e.location, calendarId: e.calendar_id, user: e._user ?? null },
          };
        }));
      })
      .catch((err) => { setLoadError(err?.response?.data?.detail || 'Could not load events'); fail(err); });
  }, [target, calendarId, calById, colors, myPersonalId]);

  const openCreate = (startWall, endWall, allDay) => {
    if (!choices.length) { window.alert('There is no calendar you can add to here.'); return; }
    const timedStart = allDay ? `${startWall}T09:00:00` : startWall;
    let timedEnd = allDay ? `${startWall}T10:00:00` : endWall;
    if (!allDay && wallDiffMinutes(timedStart, timedEnd) <= 30) timedEnd = addMinutesWall(timedStart, 60);   // one slot click = 1 hour
    setDialog({
      mode: 'create',
      createDefaults: {
        title: '', allDay,
        startDate: wallDate(startWall), startTime: timedStart.slice(11, 16),
        endDate: allDay ? addDays(wallDate(endWall), -1) : wallDate(timedEnd), endTime: timedEnd.slice(11, 16),
        location: '', notes: '', reminder: '15', reminderCustom: false, repeat: 'none', calendarId: choices[0].id,
      },
    });
  };

  const openObject = async (objectId, notice, user) => {
    const who = user === undefined ? target : user;
    try {
      const r = await calendarApi.object(objectId, who);
      setDialog({ mode: 'edit', detail: r.data, notice, user: who });
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Could not open this event');
    }
  };

  useEffect(() => {
    if (openRequest && openRequest.objectId && !openRequest.used) {   // one-shot: a later remount never reopens it
      openRequest.used = true;
      openObject(openRequest.objectId);
    }
  }, [openRequest]);   // eslint-disable-line react-hooks/exhaustive-deps

  const handleConflict = (e) => {
    const d = e?.response?.data;
    if (e?.response?.status === 409 && d) {
      if (d.deleted) { setDialog(null); refetch(); window.alert(d.detail); return true; }
      if (d.object) { setDialog((p) => ({ mode: 'edit', detail: d.object, notice: d.detail, user: p ? p.user : target })); refetch(); return true; }
    }
    return false;
  };

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) {
      if (!handleConflict(e)) setDialog((p) => p && ({ ...p, error: e?.response?.data?.detail || 'Something went wrong' }));
    } finally { setBusy(false); }
  };

  const onSave = (payload) => run(async () => {
    if (dialog.mode === 'create') {
      await calendarApi.createEvent(payload, target);
    } else {
      await calendarApi.update(dialog.detail.object_id, dialog.detail.etag, payload, dialog.user === undefined ? target : dialog.user);
    }
    setDialog(null); refetch(); onChanged && onChanged();
  });
  const onDelete = () => run(async () => {
    await calendarApi.remove(dialog.detail.object_id, dialog.detail.etag, dialog.user === undefined ? target : dialog.user);
    setDialog(null); refetch(); onChanged && onChanged();
  });
  const onCopy = (to) => run(async () => {
    const r = await calendarApi.copy(dialog.detail.object_id, to, dialog.user === undefined ? target : dialog.user);
    refetch(); onChanged && onChanged();
    setDialog((p) => ({ ...p, notice: `Copied to ${r.data.calendar.name}. The copy is separate — editing one never changes the other.`, error: null }));
  });

  // drag / resize of a non-repeating event
  const onMove = async (info) => {
    const ev = info.event;
    const allDay = ev.allDay;
    const start = allDay ? dateToWall(ev.start).slice(0, 10) : dateToWall(ev.start);
    const end = ev.end ? (allDay ? dateToWall(ev.end).slice(0, 10) : dateToWall(ev.end)) : (allDay ? addDays(start, 1) : start);
    try {
      await calendarApi.update(ev.extendedProps.objectId, ev.extendedProps.etag, { start, end, all_day: allDay }, ev.extendedProps.user ?? target);
      refetch(); onChanged && onChanged();
    } catch (e) {
      info.revert();
      const d = e?.response?.data;
      window.alert(d?.detail || 'Could not move the event');
      refetch();
    }
  };

  const meId = overview?.me?.personal_calendar_id;
  const canCopyToMine = !!(dialog && dialog.detail && meId && dialog.detail.calendar.id !== meId);

  return (
    <div className="idcal" style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {loadError && <div style={{ color: '#fca5a5', fontSize: 12, padding: '0 0 6px' }}>{loadError}</div>}
      <div style={{ flex: 1, minHeight: 0 }}>
        <FullCalendar
          ref={ref}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView={initialView || 'dayGridMonth'}
          initialDate={initialDate || undefined}
          timeZone="UTC"
          now={fcNow}
          headerToolbar={paneMode ? false : { left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek' }}
          buttonText={{ today: 'Today', month: 'Month', week: 'Week', day: 'Day', list: 'List' }}
          height="100%"
          nowIndicator
          selectable
          selectMirror
          dayMaxEvents={!showAll}
          scrollTime="07:00:00"
          eventTimeFormat={shortTimeFormat}
          eventContent={monthEventContent}
          events={fetchEvents}
          datesSet={(arg) => { if (onDatesSet) onDatesSet(arg); }}
          select={(info) => {
            info.view.calendar.unselect();
            openCreate(info.allDay ? info.startStr.slice(0, 10) : dateToWall(info.start),
              info.allDay ? info.endStr.slice(0, 10) : dateToWall(info.end), info.allDay);
          }}
          eventClick={(info) => { info.jsEvent.preventDefault(); openObject(info.event.extendedProps.objectId, undefined, info.event.extendedProps.user ?? target); }}
          eventDrop={onMove}
          eventResize={onMove}
          eventDidMount={(info) => {
            const loc = info.event.extendedProps.location;
            const cal = calById[info.event.extendedProps.calendarId];
            info.el.title = [info.event.title, loc, cal && cal.name].filter(Boolean).join(' · ');
          }}
        />
      </div>
      {dialog && (
        <CalendarEventDialog
          mode={dialog.mode} detail={dialog.detail} createDefaults={dialog.createDefaults}
          calendarChoices={choices} canCopyToMine={canCopyToMine}
          busy={busy} error={dialog.error} notice={dialog.notice}
          onSave={onSave} onDelete={onDelete} onCopy={onCopy} onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
